import type { AnyAction } from 'redux';
import { v4 as uuidv4 } from 'uuid';

import { deepCopy } from '../../../../../shared/utils';
import { reducerWrapper } from '../../../main/support/utils';
import {
  AD_LINK_UPDATE_SELECTED_TEMPLATE,
  CLEAR_AD_LINK,
  CREATE_AD_LINK,
  FETCH_AD_LINK_COLLECTIONS,
  FETCH_AVAILABLE_AD_PARTNERS,
  SEARCH_ANDROID_APP_NAME,
  SEARCH_IOS_APP_NAME,
  UPDATE_ALIAS,
  UPDATE_ANDROID,
  UPDATE_ANDROID_REDIRECT,
  UPDATE_CAMPAIGN,
  UPDATE_CHANNEL,
  UPDATE_DESKTOP,
  UPDATE_DESKTOP_REDIRECT,
  UPDATE_EDITED_AD_LINK,
  UPDATE_IOS,
  UPDATE_IOS_REDIRECT,
  UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED,
  UPDATE_IS_BLACKLIST_ERROR,
  UPDATE_IS_EDITING,
  UPDATE_IS_GENERAL_STEP_VISITED,
  UPDATE_IS_HOUSEHOLD_MEASUREMENT_ENABLED,
  UPDATE_IS_LINK_DATA_STEP_VISITED,
  UPDATE_IS_REDIRECTS_STEP_VISITED,
  UPDATE_KIND,
  UPDATE_LINK_DATA,
  UPDATE_ORIGINAL_WEB_URL,
  UPDATE_OS_PLATFORM,
  UPDATE_PARTNER,
  UPDATE_SELECTED_AD_LINK,
  UPDATE_STEP,
  UPDATE_TAGS,
  UPDATE_TITLE,
} from './actions';
import type { AdLinkCreatorState } from './types/ReducerTypes';

export const INIT_STATE: AdLinkCreatorState = {
  step: 'general',
  isGeneralStepVisited: false,
  isAnalyticsTagsStepVisited: false,
  isRedirectsStepVisited: false,
  isLinkDataStepVisited: false,
  isHouseholdMeasurementEnabled: false,
  alias: undefined,
  kind: 'app',
  title: undefined,
  partner: undefined,
  originalWebUrl: undefined,
  osPlatform: undefined,
  channel: undefined,
  campaign: undefined,
  tags: [],
  iosRedirect: 'default',
  androidRedirect: 'default',
  desktopRedirect: 'default',
  ios: undefined,
  android: undefined,
  desktop: undefined,
  linkData: [{ id: uuidv4(), key: undefined, value: undefined }],
  isBlacklistError: false,
  isEditing: false,
  editedAdLink: undefined,
  createdAdLink: undefined,
  availableAdPartners: [],
  adLinkCollections: undefined,
  osTypeEnabledLookup: {},
  domain: undefined,
};

const Handler = (state: AdLinkCreatorState, action: AnyAction): AdLinkCreatorState => {
  switch (action.type) {
    case CLEAR_AD_LINK.response:
      return { ...deepCopy(INIT_STATE) };
    case UPDATE_STEP.response:
      return {
        ...state,
        step: action.data?.step,
      };
    case UPDATE_IS_GENERAL_STEP_VISITED.response:
      return {
        ...state,
        isGeneralStepVisited: action.data?.isGeneralStepVisited,
      };
    case UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED.response:
      return {
        ...state,
        isAnalyticsTagsStepVisited: action.data?.isAnalyticsTagsStepVisited,
      };
    case UPDATE_IS_REDIRECTS_STEP_VISITED.response:
      return {
        ...state,
        isRedirectsStepVisited: action.data?.isRedirectsStepVisited,
      };
    case UPDATE_IS_LINK_DATA_STEP_VISITED.response:
      return {
        ...state,
        isLinkDataStepVisited: action.data?.isLinkDataStepVisited,
      };
    case UPDATE_ALIAS.response:
      return {
        ...state,
        alias: action.data?.alias,
      };
    case UPDATE_KIND.response:
      return {
        ...state,
        kind: action.data?.kind,
      };
    case UPDATE_TITLE.response:
      return {
        ...state,
        title: action.data?.title,
      };
    case UPDATE_PARTNER.response:
      return {
        ...state,
        partner: action.data?.partner,
      };
    case UPDATE_ORIGINAL_WEB_URL.response:
      return {
        ...state,
        originalWebUrl: action.data?.originalWebUrl,
      };
    case UPDATE_OS_PLATFORM.response:
      return {
        ...state,
        osPlatform: action.data?.osPlatform,
      };
    case UPDATE_IS_HOUSEHOLD_MEASUREMENT_ENABLED.response:
      return {
        ...state,
        isHouseholdMeasurementEnabled: action.data?.isHouseholdMeasurementEnabled,
      };
    case UPDATE_CHANNEL.response:
      return {
        ...state,
        channel: action.data?.channel,
      };
    case UPDATE_CAMPAIGN.response:
      return {
        ...state,
        campaign: action.data?.campaign,
      };
    case UPDATE_TAGS.response:
      return {
        ...state,
        tags: action.data?.tags,
      };
    case UPDATE_IOS_REDIRECT.response:
      return {
        ...state,
        iosRedirect: action.data?.iosRedirect,
      };
    case UPDATE_ANDROID_REDIRECT.response:
      return {
        ...state,
        androidRedirect: action.data?.androidRedirect,
      };
    case UPDATE_DESKTOP_REDIRECT.response:
      return {
        ...state,
        desktopRedirect: action.data?.desktopRedirect,
      };
    case UPDATE_IOS.response:
      return {
        ...state,
        ios: action.data?.ios,
      };
    case UPDATE_ANDROID.response:
      return {
        ...state,
        android: action.data?.android,
      };
    case UPDATE_DESKTOP.response:
      return {
        ...state,
        desktop: action.data?.desktop,
      };
    case SEARCH_IOS_APP_NAME.response:
      return {
        ...state,
        iosAppName: action.data?.iosAppName,
      };
    case SEARCH_ANDROID_APP_NAME.response:
      return {
        ...state,
        androidAppName: action.data?.androidAppName,
      };
    case UPDATE_LINK_DATA.response:
      return {
        ...state,
        linkData: action.data?.linkData,
      };
    case UPDATE_IS_BLACKLIST_ERROR.response:
      return {
        ...state,
        isBlacklistError: action.data?.isBlacklistError,
      };
    case UPDATE_IS_EDITING.response:
      return {
        ...state,
        isEditing: action.data?.isEditing,
      };
    case UPDATE_SELECTED_AD_LINK.response:
      return {
        ...state,
        selectedAdLink: action.data?.selectedAdLink,
      };
    case UPDATE_EDITED_AD_LINK.response:
      return {
        ...state,
        editedAdLink: action.data?.editedAdLink,
      };
    case CREATE_AD_LINK.response:
      return {
        ...state,
        createdAdLink: action.data?.createdAdLink,
      };
    case AD_LINK_UPDATE_SELECTED_TEMPLATE.response: {
      if (!action.data?.selectedTemplate) {
        return {
          ...INIT_STATE,
          alias: state.alias,
          isEditing: state.isEditing,
          title: state.title,
        };
      }

      return {
        ...state,
        selectedTemplate: action.data?.selectedTemplate,
      };
    }
    case FETCH_AVAILABLE_AD_PARTNERS.response: {
      return {
        ...state,
        availableAdPartners: action.data?.availableAdPartners || [],
        osTypeEnabledLookup: action.data?.osTypeEnabledLookup || {},
      };
    }
    case FETCH_AD_LINK_COLLECTIONS.response: {
      return {
        ...state,
        adLinkCollections: action.data?.adLinkCollections,
      };
    }

    default:
      return state;
  }
};

const TypelessHandler = (state: any, action: any) => {
  return Handler(state, action);
};

const AdLinkCreatorReducer = reducerWrapper(INIT_STATE, TypelessHandler, {
  request: [
    CLEAR_AD_LINK,
    UPDATE_STEP,
    UPDATE_IS_GENERAL_STEP_VISITED,
    UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED,
    UPDATE_IS_LINK_DATA_STEP_VISITED,
    UPDATE_IS_REDIRECTS_STEP_VISITED,
    UPDATE_ALIAS,
    UPDATE_KIND,
    UPDATE_TITLE,
    UPDATE_PARTNER,
    UPDATE_ORIGINAL_WEB_URL,
    UPDATE_OS_PLATFORM,
    UPDATE_CHANNEL,
    UPDATE_CAMPAIGN,
    UPDATE_TAGS,
    UPDATE_IOS_REDIRECT,
    UPDATE_ANDROID_REDIRECT,
    UPDATE_DESKTOP_REDIRECT,
    UPDATE_IOS,
    UPDATE_ANDROID,
    UPDATE_DESKTOP,
    SEARCH_IOS_APP_NAME,
    SEARCH_ANDROID_APP_NAME,
    UPDATE_LINK_DATA,
    UPDATE_IS_BLACKLIST_ERROR,
    UPDATE_IS_EDITING,
    UPDATE_SELECTED_AD_LINK,
    UPDATE_EDITED_AD_LINK,
    CREATE_AD_LINK,
    FETCH_AVAILABLE_AD_PARTNERS,
    FETCH_AD_LINK_COLLECTIONS,
    UPDATE_IS_HOUSEHOLD_MEASUREMENT_ENABLED,
  ],
  error: [
    CLEAR_AD_LINK,
    UPDATE_STEP,
    UPDATE_IS_GENERAL_STEP_VISITED,
    UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED,
    UPDATE_IS_LINK_DATA_STEP_VISITED,
    UPDATE_IS_REDIRECTS_STEP_VISITED,
    UPDATE_ALIAS,
    UPDATE_KIND,
    UPDATE_TITLE,
    UPDATE_PARTNER,
    UPDATE_ORIGINAL_WEB_URL,
    UPDATE_OS_PLATFORM,
    UPDATE_CHANNEL,
    UPDATE_CAMPAIGN,
    UPDATE_TAGS,
    UPDATE_IOS_REDIRECT,
    UPDATE_ANDROID_REDIRECT,
    UPDATE_DESKTOP_REDIRECT,
    UPDATE_IOS,
    UPDATE_ANDROID,
    UPDATE_DESKTOP,
    SEARCH_IOS_APP_NAME,
    SEARCH_ANDROID_APP_NAME,
    UPDATE_LINK_DATA,
    UPDATE_IS_BLACKLIST_ERROR,
    UPDATE_IS_EDITING,
    UPDATE_SELECTED_AD_LINK,
    UPDATE_EDITED_AD_LINK,
    CREATE_AD_LINK,
    FETCH_AVAILABLE_AD_PARTNERS,
    FETCH_AD_LINK_COLLECTIONS,
    UPDATE_IS_HOUSEHOLD_MEASUREMENT_ENABLED,
  ],
});

export default AdLinkCreatorReducer;
